// Unlock / tutorial trigger predicates (IMPLEMENTATION §0.4, M4a). Pure: every trigger reads the state only —
// live facts (items on the board, deposit levels, income) or the sticky progress counters that record
// event-like facts (the first merge, the first sale, the best ammo tier fired, readout streaks). Used by the
// phase-7 engine (unlocks.ts, systems/progressSystem.ts) and by the query views.
//   start          the game has started (the start state applies `start` unlocks; the engine never does)
//   practiceEmpty  the cold-open tin is used up: a Gallery exists and every Gallery has left practice mode
//                  with no rounds left (R9: the tap after the last round clicks empty and ends practice mode)
//   itemCount      ≥ n loose items of the family on the board (any tier)
//   firstMerge     a loose-item merge by the player happened (hand, Quick Merge or Merge All)
//   firstSale      an undoable sale happened (counter, Sell tool, Crate "Sell all")
//   depositLevel   a deposit of the ore at level ≥ L
//   made           the family was made at tier ≥ minTier (discoveries: spawns, merges, crafts, grants)
//   madeBy         a machine of that type crafted the family
//   handMerges     ≥ n hand merges (drag / tap-target)
//   built          a building of the type was completed (first builds: when the ghost completes)
//   expanded       the board mask reached that expansion
//   fired          a Gallery fired ammo of tier ≥ minTier; source `factory` = not hand-inserted
//   galleryBinding a Gallery read that binding for ≥ forTicks without a break (sampled per readout bucket)
//   autoIncome     automated income ≥ perSec over the income ring (R16)
//   unlocked       the unlock has fired
//   buildingLevel  a finished building of the type at level ≥ L
//   refused        some building currently shows that stall
//   mounted        a Gallery has a gun of tier ≥ minTier mounted
//   holdHits       ≥ n hold-to-mine hits
//   all            every listed leaf holds
import type { Int } from '../core/types.ts';
import type { SimDefs, TriggerDef, UnlockTrigger } from '../defs.ts';
import { famDef } from '../entities.ts';
import type { FamId } from '../gen/ids.ts';
import { autoPerSec } from '../income.ts';
import type { SimState } from '../state.ts';

/** Index of an expansion mask in defs.board.expansions (-1 = unknown). */
export function expansionIndex(d: SimDefs, id: string): Int {
  for (let i = 0; i < d.board.expansions.length; i++) {
    if (d.board.expansions[i]?.id === id) return i;
  }
  return -1;
}

function madeAtLeast(s: SimState, d: SimDefs, fam: FamId, minTier: Int): boolean {
  const tiers = famDef(d, fam).tierVersions.length;
  for (let t = minTier; t <= tiers; t++) {
    if (s.progress.discovered.includes(`${fam}:${t}`)) return true;
  }
  return false;
}

export function leafHolds(s: SimState, d: SimDefs, t: TriggerDef): boolean {
  const p = s.progress;
  switch (t.type) {
    case 'start':
      return true;
    case 'practiceEmpty': {
      let any = false;
      for (const b of s.buildings) {
        if (b.type !== 'gallery') continue;
        any = true;
        if (b.manual || b.practice > 0) return false;
      }
      return any;
    }
    case 'itemCount': {
      let n = 0;
      for (const it of s.floor) if (it.fam === t.fam) n++;
      return n >= t.n;
    }
    case 'firstMerge':
      return p.merges > 0;
    case 'firstSale':
      return p.sales > 0;
    case 'depositLevel':
      for (const b of s.buildings)
        if (b.type === 'deposit' && b.ore === t.ore && b.level >= t.level) return true;
      return false;
    case 'made':
      return madeAtLeast(s, d, t.fam, t.minTier);
    case 'madeBy':
      return p.madeBy.includes(`${t.building}:${t.fam}`);
    case 'handMerges':
      return p.handMerges >= t.n;
    case 'built':
      return p.firstBuilt.includes(t.building);
    case 'expanded': {
      const i = expansionIndex(d, t.expansion);
      return i >= 0 && s.bounds >= i;
    }
    case 'fired':
      if (famDef(d, t.fam).kind !== 'ammo') return false;
      return (t.source === 'factory' ? p.firedAutoBest : p.firedBest) >= t.minTier;
    case 'galleryBinding':
      return (t.binding === 'starved' ? p.bindingT.starved : p.bindingT.gunLimited) >= t.forTicks;
    case 'autoIncome':
      return autoPerSec(s, d) >= t.perSec;
    case 'unlocked':
      return p.unlocked.includes(t.id);
    case 'buildingLevel':
      for (const b of s.buildings)
        if (b.type === t.building && b.ghost === null && b.level >= t.level) return true;
      return false;
    case 'refused':
      for (const b of s.buildings) if (b.stall === t.stall) return true;
      return false;
    case 'mounted':
      for (const b of s.buildings)
        if (b.type === 'gallery' && b.gun !== null && b.gun.tier >= t.minTier) return true;
      return false;
    case 'holdHits':
      return p.holdHits >= t.n;
  }
}

export function triggerHolds(s: SimState, d: SimDefs, t: UnlockTrigger): boolean {
  if (t.type !== 'all') return leafHolds(s, d, t);
  for (const x of t.of) if (!leafHolds(s, d, x)) return false;
  return true;
}
